import React, { useState, useEffect, useContext, useCallback } from 'react';
import { DContext } from '../../../context/DataContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsEye, BsPencil } from 'react-icons/bs';
import Loading from '../../blocks/Loading';
import AdminListFooter from '../../blocks/AdminListFooter';

function ManagePackages() {
  const { t, i18n } = useTranslation();
  const { BeURL, setLoader, currency } = useContext(DContext);
  const [packages, setPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const currentLang = ['en', 'fr', 'ts'].includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';

  // Fetch packages from the API
  const fetchPackages = useCallback(() => {
    if (BeURL) {
      setLoader(true);
      fetch(`${BeURL}/fetch-packages`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => {
          setLoader(false);
          if (data.success) {
            setPackages(data.packages);
          } else {
            alert(t('adminCommon.requestFailed'));
          }
        })
        .catch((err) => {
          setLoader(false);
          console.error('Error fetching packages:', err);
          alert(t('adminCommon.serverError'));
        });
    }
  }, [BeURL, setLoader, t]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Filter packages based on search query (searching entire package object)
  const filteredPackages = packages.filter((pkg) => {
    const pkgString = JSON.stringify(pkg).toLowerCase();
    return pkgString.includes(searchQuery.toLowerCase());
  });

  // Sort packages if sortField is provided
  if (sortField) {
    filteredPackages.sort((a, b) => {
      let valA, valB;
      if (sortField === 'title') {
        valA = (a.title?.[currentLang] || a.title?.en || '').toLowerCase();
        valB = (b.title?.[currentLang] || b.title?.en || '').toLowerCase();
      } else if (sortField === 'price') {
        // Sorting by THB price
        valA = a.price.thb.adult;
        valB = b.price.thb.adult;
      } else if (sortField === 'id') {
        valA = a.id;
        valB = b.id;
      } else {
        valA = '';
        valB = '';
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const totalItems = filteredPackages.length;
  const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
  const paginatedPackages = filteredPackages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  if (!packages && !currency) {
    return <Loading />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className='text-2xl font-extrabold tracking-tight text-[#1739bd]'>{t('manage-packages.heading')}</h2>
          <Link to="/admin/create-package" className="inline-flex items-center justify-center rounded-2xl bg-[#1739bd] px-4 py-2 text-sm font-semibold text-white no-underline shadow-lg transition hover:bg-indigo-700">
            {t('manage-packages.createPackage')}
          </Link>
        </div>

        {/* Search and sort options */}
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <input
              type="text"
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
              placeholder={t('manage-packages.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div>
            <select
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">{t('manage-packages.sortBy')}...</option>
              <option value="id">{t('manage-packages.table.id')}</option>
              <option value="title">{t('manage-packages.table.title')}</option>
              <option value="price">{t('manage-packages.table.price')}</option>
            </select>
          </div>
          <div>
            <select
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="asc">{t('manage-packages.ascending')}</option>
              <option value="desc">{t('manage-packages.descending')}</option>
            </select>
          </div>
        </div>

        {/* Packages Table */}
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-100/80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('manage-packages.table.id')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('manage-packages.table.title')}</th>
                {/* <th>{t('manage-packages.table.price')}</th> */}
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('manage-packages.table.createdAt')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('manage-packages.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {paginatedPackages.length > 0 ? (
                paginatedPackages.map((pkg) => (
                  <tr key={pkg.id}>
                    <td className="px-4 py-3 text-sm text-slate-600">{pkg.id}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{pkg.title?.[currentLang] || pkg.title?.en || '-'}</td>
                    {/* <td>
                    {
                      currency==="usd" && pkg.price.usd.adult
                    }
                    {
                      currency==="thb" && pkg.price.thb.adult
                    }
                  </td> */}
                    <td className="px-4 py-3 text-sm text-slate-600">{new Date(pkg.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/packages/${pkg.id}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200 text-[#1739bd] transition hover:bg-indigo-50"
                          title={t('manage-packages.table.view')}
                        >
                          <BsEye />
                        </Link>
                        <Link
                          to={`/admin/update-package/${pkg.id}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                          title={t('manage-packages.table.update')}
                        >
                          <BsPencil />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-sm text-slate-500">
                    {t('manage-packages.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <AdminListFooter
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      </div>
    </div>
  );
}

export default ManagePackages;
